import { zodResolver } from "@hookform/resolvers/zod";
import { useSendReportToRecipients } from "api/api-hooks/reports-hooks";
import { ControllerChips } from "components/basic/controller-inputs/ControllerInputs";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { Button } from "primereact/button";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import { T } from "services/lang/LanguageService";
import { z } from "zod";

type EmailReportFormProps = {
  recipients: string[];
  genId: number;
  reportId?: string;
  closeDialog: () => void;
};

const schema = z.object({
  recipients: z
    .array(z.string({ message: T("word.required-fields") }).email({ message: T("alert.invalid-email") }), {
      message: T("word.required-fields"),
    })
    .nonempty({ message: T("word.required-fields") }),
});

const EmailReportForm: React.FC<EmailReportFormProps> = ({ recipients, genId, reportId, closeDialog }) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<{ recipients: string[] }>({
    defaultValues: { recipients },
    resolver: zodResolver(schema),
  });

  const { mutate: sendEmails, isPending } = useSendReportToRecipients({
    onSuccess: () => {
      easasAlert({
        summary: T("alert.success"),
        detail: T("alert.report-successfully-sent"),
        severity: "success",
      });
      closeDialog();
    },
    onFailure: error => {
      easasAlert({
        summary: T("alert.error"),
        detail: error.message,
        severity: "error",
      });
    },
  });

  const onSubmit = (data: { recipients: string[] }) => {
    if (!reportId) {
      return;
    }
    sendEmails({ genId, reportId, recipients: data.recipients });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Controller
        name="recipients"
        defaultValue={[]}
        control={control}
        render={({ field }) => (
          <ControllerChips
            field={field}
            errors={errors}
            label={T("word.recipients")}
            placeholder={T("label.default-chip-placeholder")}
            className="column-chip-display"
            col={12}
            separator=", "
          />
        )}
      />

      <div className="flex justify-content-end">
        <Button label={T("side-menu.send-mail")} loading={isPending} />
      </div>
    </form>
  );
};

export default EmailReportForm;
